import React from 'react';
import { summarizeRunsToday } from '@/lib/milliData';
import { departmentFromRun } from '@/lib/milliDepartments';

/* The run ledger: what Milli did, which desk did it, what it cost. Every
 * chat turn and every automation is a kc_agent_runs row; since v50 chat
 * runs carry plan.department, so the ledger shows the desk. Open internal
 * tasks sit underneath. */

const RUN_DOT = {
  completed: 'bg-emerald-400',
  failed: 'bg-red-400',
  running: 'bg-amber-300 animate-pulse',
  pending: 'bg-amber-300 animate-pulse',
  cancelled: 'bg-stone-500',
};

const fmtTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  // Today shows the clock time; anything older shows the date, so the
  // column stays one line wide.
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const fmtUsd = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// Chat runs read "chat-web"; automations read like "cron:weekly-batch".
const sourceLabel = (s) => String(s || '').replace(/^chat-web$/, 'chat').replace(/^cron:/, '');

export default function ConsoleLedger({ runs = [], tasks = [], loading, t }) {
  const today = summarizeRunsToday(runs);

  return (
    <section data-testid="milli-console-ledger">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-stone-500">{t('milli.console.ledger')}</span>
        <span className="font-mono text-[10px] text-stone-500" data-testid="milli-ledger-today">
          {t('milli.console.today')} · {today.count} {t('milli.console.runs')} · {fmtUsd(today.cost)}
        </span>
      </div>

      <div className="px-4 pb-4">
        {runs.length === 0 ? (
          <p className="text-xs text-stone-500">{loading ? '…' : t('milli.panels.runsEmpty')}</p>
        ) : (
          <ul className="space-y-1 font-mono text-[11px]" data-testid="milli-ledger-runs">
            {runs.map((run) => {
              const dept = departmentFromRun(run);
              return (
                <li key={run.id} className="flex items-center gap-2 rounded px-1.5 py-1 text-stone-400 hover:bg-[#1a1b1d]" data-testid={`milli-run-${run.id}`}>
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${RUN_DOT[run.status] || 'bg-stone-500'}`} title={run.status} />
                  {dept ? (
                    <span className="flex items-center gap-1.5 min-w-0" style={{ color: dept.hex }} title={dept.pillar}>
                      <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: dept.hex }} />
                      <span className="truncate">{dept.name}</span>
                    </span>
                  ) : (
                    <span className="text-stone-300 truncate">{sourceLabel(run.trigger_source)}</span>
                  )}
                  {dept && run.trigger_source !== 'chat-web' ? (
                    <span className="text-stone-600 truncate">{sourceLabel(run.trigger_source)}</span>
                  ) : null}
                  <span className="ml-auto shrink-0 text-stone-600">{fmtUsd(run.cost_usd)}</span>
                  <span className="shrink-0 whitespace-nowrap text-stone-500 min-w-[62px] text-right">{fmtTime(run.started_at)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="px-4 pb-2 font-mono text-[10px] uppercase tracking-[0.24em] text-stone-500">{t('milli.panels.tasks')}</div>
      <div className="px-4 pb-6" data-testid="milli-tasks-panel">
        {tasks.length === 0 ? (
          <p className="text-xs text-stone-500">{loading ? '…' : t('milli.panels.tasksEmpty')}</p>
        ) : (
          <ul className="space-y-1.5">
            {tasks.slice(0, 8).map((task) => (
              <li key={task.id} className="text-xs leading-snug">
                <span className="text-stone-200">{task.title}</span>
                <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">{task.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
